import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import React from 'react';
import {
  BooleanField,
  ImageField,
  Labeled,
  NumberField,
  ReferenceArrayField,
  SelectField,
  Show,
  ShowButton,
  Tab,
  TabbedShowLayout,
  TextField,
  useNotify,
  useRecordContext,
} from 'react-admin';
import {
  AdminClientDetailsFragment,
  AdminCreateTestUserDocument,
  AdminListFeaturesDocument,
  ClientConfigurationIssuesDocument,
} from '../../../graphql/types';
import useClientCoachesQuery from '../../../hooks/useClientCoachesQuery';
import { SplitFlags, useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { ConfigurationIssueItem } from '../components/ConfigurationIssueItem';
import CopyIconButton from '../components/CopyIconButton';
import { ListCoaches } from '../components/ListCoaches';
import { WriteOnlyDatagrid } from '../components/WriteOnlyDatagrid';
import { ScopedFeatureGrants } from '../FeatureGrant';
import * as Labels from './labels';

const ClientCoachTab: React.FC = () => {
  const record = useRecordContext<AdminClientDetailsFragment>();
  const { coaches, loadMore, hasNextPage, isFetchingMore } =
    useClientCoachesQuery(record?.id);

  return (
    <ListCoaches
      coaches={coaches}
      clientId={record?.id}
      loadMore={loadMore}
      hasNextPage={hasNextPage}
      isFetchingMore={isFetchingMore}
    />
  );
};

const DiagnosticsTab: React.FC = () => {
  const record = useRecordContext<AdminClientDetailsFragment>();
  const clientId = record?.id;

  const notify = useNotify();
  const [runningDiagnostics, setRunningDiagnostics] = React.useState(false);
  const [fetchIssues, { data }] = useLazyQuery(
    ClientConfigurationIssuesDocument,
  );

  const handleClick = async () => {
    if (!clientId) {
      return;
    }

    setRunningDiagnostics(true);
    try {
      await fetchIssues({
        variables: {
          id: clientId,
        },
      });
    } catch (error) {
      notify(
        `Failed to run diagnostics: ${error instanceof Error ? error.message : String(error)}`,
        { type: 'error' },
      );
    } finally {
      setRunningDiagnostics(false);
    }
  };

  const configurationIssues = data?.client?.configurationIssues || [];
  return (
    <>
      <Box>
        <Typography variant="h4">Client Diagnostics</Typography>
        <p>
          Press the button below to perform a series of checks on this client's
          configuration. These checks are not comprehensive and are currently
          focused on the following items:
        </p>
        <ul>
          <li>
            Ensure that the provided MBO Studio ID for each non-central studio
            exists in MBO
          </li>
          <li>Ensure that the client has at least one assignable coach</li>
          <li>
            For clients that have "Uses MBO Registration Flow" enabled this will
            check for the following:
            <ul>
              <li>There are in-person studios available for registration</li>
              <li>
                There is at least one valid location with an assigned Hybrid
                coach
              </li>
              <li>The MBO contract is available for each location</li>
              <li>
                The provided MBO studio ID exists in MBO and has a contact email
              </li>
            </ul>
          </li>
        </ul>
        <Button
          onClick={handleClick}
          loading={runningDiagnostics}
          variant="contained"
        >
          Run Diagnostics
        </Button>
      </Box>
      <Box marginTop={2}>
        {!runningDiagnostics && data && configurationIssues.length === 0 && (
          <Typography variant="body1" color="success.main">
            No configuration issues found.
          </Typography>
        )}
        {!runningDiagnostics && (
          <div style={{ width: 'fit-content' }}>
            {configurationIssues.length > 0 && (
              <Typography variant="body1" color="error.main">
                Found the following configuration issue
                {configurationIssues.length > 1 ? 's' : ''}:
              </Typography>
            )}
            {configurationIssues.map((issue) => (
              <ConfigurationIssueItem issue={issue} key={issue.title} />
            ))}
          </div>
        )}
      </Box>
    </>
  );
};

type TestUserResult = {
  email: string;
  password: string;
  expiresAt: string;
};

const ReadOnlyCopyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box display="flex" alignItems="center" gap={1} marginTop={2}>
    <MuiTextField
      label={label}
      value={value}
      size="small"
      fullWidth
      slotProps={{ input: { readOnly: true } }}
    />
    <CopyIconButton text={value} />
  </Box>
);

const CreateTestUserResultDialog: React.FC<{
  open: boolean;
  result: TestUserResult | null;
  onClose: () => void;
  onExited: () => void;
}> = ({ open, result, onClose, onExited }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    // Keep the credentials rendered through the close animation; clear them
    // only once the dialog has fully exited (avoids a flicker on close).
    slotProps={{ transition: { onExited } }}
  >
    <DialogTitle>Test user created</DialogTitle>
    <DialogContent>
      <Typography variant="body2" color="error.main">
        This password is shown only once. Copy it now — it cannot be retrieved
        again.
      </Typography>
      {result && (
        <>
          <ReadOnlyCopyField label="Username (email)" value={result.email} />
          <ReadOnlyCopyField label="Password" value={result.password} />
          <ReadOnlyCopyField label="Expires at" value={result.expiresAt} />
        </>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// The catalog is a handful of rows; one page covers it (same bound as the
// member effective-features view).
const FEATURE_CATALOG_PAGE_SIZE = 100;

// Local "YYYY-MM-DDTHH:mm" string for a datetime-local input's min attribute.
const toDateTimeLocal = (date: Date): string =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);

export const CreateTestUserSection: React.FC = () => {
  const record = useRecordContext<AdminClientDetailsFragment>();
  const notify = useNotify();
  const clientId = record?.id;

  const [expiryMode, setExpiryMode] = React.useState<'7' | '30' | 'custom'>(
    '7',
  );
  const [customExpiry, setCustomExpiry] = React.useState('');
  const [selectedFeatureIds, setSelectedFeatureIds] = React.useState<string[]>(
    [],
  );
  const [result, setResult] = React.useState<TestUserResult | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const { flags } = useFeatureFlags();
  const enableFeatureManagement = flags[SplitFlags.SPA_FEATURE_MANAGEMENT];
  const { data: catalog } = useQuery(AdminListFeaturesDocument, {
    variables: {
      filter: { isArchived: false },
      input: { first: FEATURE_CATALOG_PAGE_SIZE },
    },
    fetchPolicy: 'cache-first',
    skip: !enableFeatureManagement,
  });
  const featureOptions = enableFeatureManagement
    ? (catalog?.data.nodes ?? [])
    : [];

  const toggleFeature = (featureId: string, checked: boolean) =>
    setSelectedFeatureIds((prev) =>
      checked ? [...prev, featureId] : prev.filter((id) => id !== featureId),
    );

  const [createTestUser, { loading }] = useMutation(
    AdminCreateTestUserDocument,
    {
      onCompleted: (data) => {
        if (data.createTestUser) {
          setResult({
            email: data.createTestUser.email,
            password: data.createTestUser.password,
            expiresAt: data.createTestUser.expiresAt,
          });
          setDialogOpen(true);
        }
      },
      onError: (error) =>
        notify(`Failed to create test user: ${error.message}`, {
          type: 'error',
        }),
    },
  );

  // Presets and the custom picker all resolve to a single absolute UTC
  // timestamp; returns null if the custom value is empty or not in the future.
  const resolveExpiresAt = (): string | null => {
    if (expiryMode === 'custom') {
      if (!customExpiry) {
        return null;
      }
      const date = new Date(customExpiry);
      if (Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) {
        return null;
      }
      return date.toISOString();
    }
    const days = expiryMode === '7' ? 7 : 30;
    return new Date(Date.now() + days * MS_PER_DAY).toISOString();
  };

  const handleClick = () => {
    if (!clientId) {
      return;
    }
    const expiresAt = resolveExpiresAt();
    if (!expiresAt) {
      notify('Choose a valid future expiry date', { type: 'warning' });
      return;
    }
    createTestUser({
      variables: {
        input: {
          clientId,
          expiresAt,
          featureIds:
            enableFeatureManagement && selectedFeatureIds.length > 0
              ? selectedFeatureIds
              : undefined,
        },
      },
    });
  };

  return (
    <Box marginTop={2}>
      <Typography variant="subtitle2">Create Test User</Typography>
      <Typography variant="body2" color="text.secondary" marginTop={1}>
        Creates a ready-to-use test account for this client:
      </Typography>
      <Typography
        component="ul"
        variant="body2"
        color="text.secondary"
        sx={{ marginTop: 0.5, marginBottom: 0, paddingLeft: 2.5 }}
      >
        <li>
          Fully-registered user that can log in with a generated email +
          password
        </li>
        <li>
          Expires when you choose — then it can’t sign in and is auto-deleted
        </li>
        <li>Password is shown once and can’t be retrieved again</li>
      </Typography>
      {featureOptions.length > 0 && (
        <Box marginTop={1}>
          <Typography variant="body2" color="text.secondary">
            Enable features for the account (written as user-scope grants):
          </Typography>
          <FormGroup row>
            {featureOptions.map((feature) => (
              <FormControlLabel
                key={feature.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedFeatureIds.includes(feature.id)}
                    onChange={(e) =>
                      toggleFeature(feature.id, e.target.checked)
                    }
                  />
                }
                label={feature.name}
              />
            ))}
          </FormGroup>
        </Box>
      )}
      <Box display="flex" alignItems="center" gap={2} marginTop={1}>
        <MuiTextField
          select
          label="Expiry"
          value={expiryMode}
          onChange={(e) => {
            const mode = e.target.value as '7' | '30' | 'custom';
            setExpiryMode(mode);
            // Prefill Custom with a sensible default (7 days out) so a full
            // date/time doesn't have to be entered from scratch.
            if (mode === 'custom' && !customExpiry) {
              setCustomExpiry(
                toDateTimeLocal(new Date(Date.now() + 7 * MS_PER_DAY)),
              );
            }
          }}
          size="small"
          style={{ minWidth: 140 }}
        >
          <MenuItem value="7">7 days</MenuItem>
          <MenuItem value="30">30 days</MenuItem>
          <MenuItem value="custom">Custom</MenuItem>
        </MuiTextField>
        {expiryMode === 'custom' && (
          <MuiTextField
            type="datetime-local"
            label="Expires at"
            value={customExpiry}
            onChange={(e) => setCustomExpiry(e.target.value)}
            size="small"
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: toDateTimeLocal(new Date()) },
            }}
          />
        )}
        <Button onClick={handleClick} loading={loading} variant="contained">
          Create Test User
        </Button>
      </Box>
      <CreateTestUserResultDialog
        open={dialogOpen}
        result={result}
        onClose={() => setDialogOpen(false)}
        onExited={() => setResult(null)}
      />
    </Box>
  );
};

const ClientFeatureGrantsTab: React.FC = () => {
  const record = useRecordContext<AdminClientDetailsFragment>();
  return record ? (
    <ScopedFeatureGrants
      scopeType="client"
      scopeId={record.id}
      clientId={record.id}
      storeKey={`client-${record.id}-feature-grants`}
      showCleanupLink
    />
  ) : null;
};

export const ClientShow: React.FC = () => {
  const { flags } = useFeatureFlags();
  return (
    <Show>
      <TabbedShowLayout>
        <Tab label="Basics">
          <TextField source="id" label="ID" data-testid="id" />
          <TextField source="name" label="Client Name" data-testid="name" />
          <TextField
            source="displayName"
            label="Display Name"
            data-testid="displayName"
          />
          <ImageField source="logo.url" label="Logo" />
          <Typography variant="subtitle2">
            {Labels.SUBTITLE_INTEGRATIONS}
          </Typography>
          <TextField
            source="calendarPlatform"
            label="Calendar Platform"
            data-testid="calendarPlatform"
          />
          <TextField
            source="videoPlatform"
            label="Video Platform"
            data-testid="videoPlatform"
          />
          <Typography variant="subtitle2">
            {Labels.SUBTITLE_IMPORT_HISTORY}
          </Typography>
          <TextField
            source="users.lastMboImportDate"
            label={Labels.LAST_MBO_IMPORT_DATE}
            data-testid="lastMboImport"
          />
          <TextField
            source="users.lastFileImportDate"
            label={Labels.LAST_FILE_IMPORT_DATE}
            data-testid="lastFileImport"
          />

          <Typography variant="subtitle2">
            {Labels.SUBTITLE_OPERATIONS}
          </Typography>
          <BooleanField
            source="isArchived"
            label={Labels.IS_ARCHIVED}
            data-testid="isArchived"
          />
          <BooleanField
            source="isFcmOnly"
            label={Labels.IS_FCM_ONLY}
            data-testid="isFcmOnly"
          />
          <BooleanField
            source="usesSSO"
            data-testid="usesSSO"
            label={Labels.USES_SSO}
          />

          <Typography variant="subtitle2">Linked Entities</Typography>
          <Labeled label="Domains" fullWidth>
            <ReferenceArrayField source="domains.ids" reference="Domain">
              <WriteOnlyDatagrid>
                <TextField source="domain" />
                <ShowButton />
              </WriteOnlyDatagrid>
            </ReferenceArrayField>
          </Labeled>
          <Labeled label="Studios" fullWidth>
            <ReferenceArrayField source="studios.ids" reference="Studio">
              <WriteOnlyDatagrid>
                <TextField source="name" />
                <BooleanField source="isVirtual" label="Virtual" />
                <BooleanField source="isArchived" label="Archived" />
                <ShowButton />
              </WriteOnlyDatagrid>
            </ReferenceArrayField>
          </Labeled>
        </Tab>
        <Tab label="Configs" data-testid="tab-Fit">
          <Typography variant="subtitle2">
            {Labels.SUBTITLE_ELIGIBILITY}
          </Typography>
          <BooleanField
            source="usesFitnessCenterForEligibility"
            data-testid="usesFitnessCenterForEligibility"
            label={Labels.USES_FITNESS_CENTER_FOR_ELIGIBILITY}
          />
          <BooleanField
            source="requiresEligibleEmailForGym"
            data-testid="requiresEligibleEmailForGym"
            label={Labels.REQUIRES_ELIGIBLE_EMAIL_FOR_GYM}
          />
          <BooleanField
            source="usesEligibilityServiceType"
            data-testid="usesEligibilityServiceType"
            label={Labels.USES_ELIGIBILITY_SERVICE_TYPE}
          />

          <Typography variant="subtitle2">
            {Labels.SUBTITLE_OPT_IN_FEATURES}
          </Typography>
          <BooleanField
            source="usesPersonalTraining"
            data-testid="usesPersonalTraining"
            label={Labels.USES_PERSONAL_TRAINING}
            valueLabelTrue="Supports personal training sessions"
            valueLabelFalse="Does not support personal training sessions"
          />
          <BooleanField
            source="usesHighPerformanceCoaching"
            data-testid="usesHighPerformanceCoaching"
            label={Labels.USES_HIGH_PERFORMANCE_COACHING}
            valueLabelTrue="Supports high-performance coaching"
            valueLabelFalse="Does not support high-performance coaching"
          />
          {/* usesMboSso is a legacy field name — this toggle has nothing to do with SSO.
            It prevents MBO password reset emails from being sent when a client uses One Platform. */}
          <BooleanField
            source="usesMboSso"
            data-testid="usesMboSso"
            label={Labels.USES_MBO_SSO}
          />
          <BooleanField
            source="usesCustomChallenges"
            data-testid="usesCustomChallenges"
            label={Labels.USES_CUSTOM_CHALLENGES}
          />
          <SelectField
            source="serviceSlotIncrement"
            choices={Labels.SERVICE_SLOT_INCREMENT_CHOICES}
            data-testid="serviceSlotIncrement"
            label={Labels.SERVICE_SLOT_INCREMENT}
          />
          <Typography variant="subtitle2">
            {Labels.SUBTITLE_CLIENT_OFFERINGS}
          </Typography>
          <NumberField
            source="maxFamilyMembers"
            label={Labels.MAX_FAMILY_MEMBERS}
            data-testid="maxFamilyMembers"
            emptyText="0"
          />
          <BooleanField
            source="collectsMemberData"
            data-testid="collectsMemberData"
            label={Labels.COLLECTS_MEMBER_DATA}
          />
          <BooleanField
            source="collectsMemberDataForEEP"
            data-testid="collectsMemberDataForEEP"
            label={Labels.COLLECTS_MEMBER_DATA_FOR_EEP}
          />

          <Typography variant="subtitle2">
            {Labels.SUBTITLE_BIOMETRICS}
          </Typography>
          <BooleanField
            source="biometricsEnabled"
            data-testid="biometricsEnabled"
            label={Labels.BIOMETRICS_ENABLED}
          />
          <NumberField
            source="biometricHardSignOutDuration"
            data-testid="biometricHardSignOutDuration"
            label={Labels.BIOMETRIC_HARD_SIGN_OUT_DURATION}
            emptyText="Default (28)"
          />
          <NumberField
            source="biometricInactivityTimeout"
            data-testid="biometricInactivityTimeout"
            label={Labels.BIOMETRIC_INACTIVITY_TIMEOUT}
            emptyText="Default (30)"
          />

          <Typography variant="subtitle2">
            {Labels.SUBTITLE_OTHER_CONFIGS}
          </Typography>
          <BooleanField
            source="allowsCoachesToCreateMembers"
            label={Labels.ALLOWS_COACHES_TO_CREATE_MEMBERS}
            data-testid="allowsCoachesToCreateMembers"
          />
          <NumberField
            source="inactivityDuration"
            data-testid="inactivityDuration"
            label={Labels.INACTIVITY_DURATION}
            emptyText="N/A"
          />
          <BooleanField
            source="strictAppCheck"
            label={Labels.STRICT_APP_CHECK}
            data-testid="strictAppCheck"
          />
          <BooleanField
            source="needsUsToFixTheirUserNames"
            data-testid="needsUsToFixTheirUserNames"
            label={Labels.NEEDS_US_TO_FIX_THEIR_USERNAMES}
            valueLabelTrue="Names need to be reformatted (ex. BRAD -> Brad)"
            valueLabelFalse="Names are already correctly formatted (ex. Brad not BRAD)"
          />
        </Tab>
        <Tab label="Coaches" data-testid="tab-Coaches">
          <ClientCoachTab />
        </Tab>
        <Tab label="Testing" data-testid="tab-Testing">
          <DiagnosticsTab />
          <CreateTestUserSection />
        </Tab>
        {flags[SplitFlags.SPA_FEATURE_MANAGEMENT] && (
          <Tab label="Feature Grants">
            <ClientFeatureGrantsTab />
          </Tab>
        )}
      </TabbedShowLayout>
    </Show>
  );
};
